import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { DEFAULT_WA_TEMPLATES, fillTemplate, setWaCountryCode, waDisplay, waLink, waLinkWithText } from "@/lib/whatsapp";
import { MessageSquare, PhoneCall } from "lucide-react";

let waCache = null;
// office WhatsApp settings (templates + default country code), fetched once
export const loadWaSettings = () => {
  if (!waCache) waCache = api.get("/settings").then((r) => {
    setWaCountryCode(r.data.default_country_code || "1");
    return { templates: r.data.whatsapp_templates?.length ? r.data.whatsapp_templates : DEFAULT_WA_TEMPLATES, cc: r.data.default_country_code || "1", winback: r.data.winback_messages || {} };
  }).catch(() => ({ templates: DEFAULT_WA_TEMPLATES, cc: "1", winback: {} }));
  return waCache;
};
const loadTemplates = () => loadWaSettings().then((x) => x.templates);
export const resetWaTemplates = () => { waCache = null; };
export function useWaReady() {
  const [, setTick] = useState(0);
  useEffect(() => { loadWaSettings().then(() => setTick((t) => t + 1)); }, []);
}

// target: { kind: "client" | "lead", id, name, phone, file_number, service }
function WhatsAppDialog({ target, onClose, onLogged, preset }) {
  const { user } = useAuth();
  const [templates, setTemplates] = useState(DEFAULT_WA_TEMPLATES);
  const [step, setStep] = useState("pick");
  const [text, setText] = useState(preset?.text || "");
  const [picked, setPicked] = useState(preset?.name || "");
  const [how, setHow] = useState("call");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { loadTemplates().then(setTemplates); }, []);
  const vars = { name: target.name, first_name: (target.name || "").split(" ")[0], file_number: target.file_number || "", service: target.service || "our services", staff_name: user?.name || "" };

  const open = async () => {
    window.open(waLinkWithText(target.phone, text.trim()), "_blank", "noopener,noreferrer");
    if (preset?.autoLog && target.kind === "lead") {
      try { await api.post(`/leads/${target.id}/log-contact`, { channel: "WhatsApp message", note: preset.logNote }); toast.success("Win-back message logged on the lead"); onLogged?.(); }
      catch (e) { toast.error(apiErr(e)); }
      onClose(); return;
    }
    if (text.trim()) setHow("message"); setStep("log");
  };
  const pick = (t) => { setPicked(t ? t.name : ""); setText(t ? fillTemplate(t.body, vars) : ""); };
  const save = async () => {
    setBusy(true);
    const label = how === "call" ? "WhatsApp call" : "WhatsApp message";
    try {
      if (target.kind === "client") await api.post(`/clients/${target.id}/interactions`, { channel: "WhatsApp", note: [how === "call" ? "Call" : "Message", note.trim() || (picked && `Sent "${picked}"`)].filter(Boolean).join(" — ") });
      else await api.post(`/leads/${target.id}/log-contact`, { channel: label, note: note.trim() || (picked ? `Sent "${picked}"` : "") });
      toast.success(`${label} logged`); onLogged?.(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="whatsapp-dialog" aria-describedby={undefined} onClick={(e) => e.stopPropagation()}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><WhatsAppIcon className="h-5 w-5 text-[#25D366]" /> WhatsApp {target.name}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500 -mt-2">{waDisplay(target.phone)}</div>
        {step === "pick" ? (
          <div className="space-y-3">
            {preset ? <div className="text-xs font-semibold text-[#128C7E] bg-[#25D366]/10 rounded-lg px-3 py-2" data-testid="wa-winback-label">{preset.name}</div> :
            <div className="flex flex-wrap gap-1.5" data-testid="whatsapp-templates">
              <button type="button" data-testid="wa-template-none" onClick={() => pick(null)} className={`px-3 py-1 rounded-full border text-xs font-semibold ${!picked ? "bg-[#25D366] text-white border-[#25D366]" : "border-slate-200 text-slate-600"}`}>Blank chat</button>
              {templates.map((t, i) => (
                <button key={t.name + i} type="button" data-testid={`wa-template-${i}`} onClick={() => pick(t)} className={`px-3 py-1 rounded-full border text-xs font-semibold ${picked === t.name ? "bg-[#25D366] text-white border-[#25D366]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{t.name}</button>
              ))}
            </div>}
            <textarea data-testid="wa-message" value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Optional message — leave empty to just open the chat"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#25D366]" />
            <button type="button" data-testid="wa-open" onClick={open} className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white py-2.5 rounded-xl font-semibold text-sm">
              <WhatsAppIcon className="h-4 w-4" /> {preset?.autoLog ? "Send win-back on WhatsApp" : text.trim() ? "Open WhatsApp with this message" : "Open WhatsApp chat"}
            </button>
            <p className="text-[11px] text-slate-400">To call, tap the phone icon at the top of the WhatsApp chat.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="whatsapp-log-step">
            <div className="text-sm font-semibold text-slate-800">Log this {how === "call" ? "call" : "message"}?</div>
            <div className="inline-flex bg-slate-100 rounded-xl p-1">
              {[["call", "Call", PhoneCall], ["message", "Message", MessageSquare]].map(([k, l, Icon]) => (
                <button key={k} type="button" data-testid={`wa-log-${k}`} onClick={() => setHow(k)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${how === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}><Icon className="h-3.5 w-3.5" />{l}</button>
              ))}
            </div>
            <textarea data-testid="wa-log-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What was discussed? (optional)" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
            <div className="flex gap-2 justify-end">
              <button type="button" data-testid="wa-log-skip" onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600">Skip</button>
              <button type="button" data-testid="wa-log-save" onClick={save} disabled={busy} className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-60">{busy ? "Saving…" : "Log it"}</button>
            </div>
            <p className="text-[11px] text-slate-400">{target.kind === "client" ? "Saved to the client's Notes & Activity tab." : "Saved to the lead's notes and last-contact date."}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function WhatsAppButton({ target, variant = "icon", onLogged, testid }) {
  const [open, setOpen] = useState(false);
  useWaReady();
  const ok = !!waLink(target.phone);
  const click = (e) => { e.preventDefault(); e.stopPropagation(); if (ok) setOpen(true); };
  const cls = variant === "pill"
    ? `shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${ok ? "bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20" : "bg-slate-100 text-slate-300 cursor-not-allowed"}`
    : `inline-flex items-center justify-center h-8 w-8 rounded-lg transition-colors ${ok ? "text-[#25D366] hover:text-[#128C7E] hover:bg-[#25D366]/10" : "text-slate-200 cursor-not-allowed"}`;
  return (
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <button type="button" data-testid={ok ? testid : `${testid}-disabled`} onClick={click} title={ok ? `WhatsApp ${waDisplay(target.phone)}` : "No phone on file"} className={cls}>
        <WhatsAppIcon className={variant === "pill" ? "h-3.5 w-3.5" : "h-4 w-4"} />{variant === "pill" && " WhatsApp"}
      </button>
      {open && <WhatsAppDialog target={target} onClose={() => setOpen(false)} onLogged={onLogged} />}
    </span>
  );
}

const FALLBACK_WINBACK = "Hi {first_name}, this is {staff_name} from Chahal Overseas. Just following up on your interest in {service}. Is now a better time to talk?";

// One-tap WhatsApp message tailored to why the lead was lost; sending logs the contact
export function WinBackDialog({ lead, onClose, onLogged }) {
  const { user } = useAuth();
  const [preset, setPreset] = useState(null);
  useEffect(() => {
    loadWaSettings().then(({ winback }) => {
      const reason = lead.lost_reason || "Other";
      const body = winback[reason] || winback.Other || FALLBACK_WINBACK;
      const vars = { name: lead.name, first_name: (lead.name || "").split(" ")[0], service: lead.service_interested || "your immigration plans", staff_name: user?.name || "", file_number: "" };
      setPreset({ name: `Win-back · ${reason}`, text: fillTemplate(body, vars), autoLog: true, logNote: `Win-back message sent (${reason})` });
    });
  }, [lead, user]);
  if (!preset) return null;
  return <WhatsAppDialog target={{ kind: "lead", id: lead.id, name: lead.name, phone: lead.phone, service: lead.service_interested }} preset={preset} onClose={onClose} onLogged={onLogged} />;
}

export function WinBackButton({ lead, onLogged, testid }) {
  const [open, setOpen] = useState(false);
  useWaReady();
  const ok = !!waLink(lead.phone);
  return (
    <span className="contents" onClick={(e) => e.stopPropagation()}>
      <button type="button" data-testid={ok ? testid : `${testid}-disabled`} disabled={!ok} title={ok ? "Send a win-back message on WhatsApp" : "No phone on file"}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap ${ok ? "bg-[#25D366] text-white hover:bg-[#1fb857]" : "bg-slate-100 text-slate-300 cursor-not-allowed"}`}>
        <WhatsAppIcon className="h-3.5 w-3.5" /> Win back
      </button>
      {open && <WinBackDialog lead={lead} onClose={() => setOpen(false)} onLogged={onLogged} />}
    </span>
  );
}
